/**
 * Asset Link Management Component
 *
 * Component for managing trackable links and QR codes for assets
 * with generation, editing, and analytics capabilities.
 */

import React, { useState, useCallback } from "react";
import { useAssetLinks } from "../../hooks/use-asset-links";
import {
  AssetLink,
  AssetLinkFilterInput,
  GenerateAssetLinkInput,
  QRCodeSize,
  LINK_STATUS_LABELS,
  QR_CODE_SIZE_LABELS,
} from "../../types/asset-link-types";
import { Asset } from "../../types/asset-types";

interface AssetLinkManagementProps {
  asset: Asset;
  onClose?: () => void;
}

export function AssetLinkManagement({
  asset,
  onClose,
}: AssetLinkManagementProps) {
  const [filter, setFilter] = useState<AssetLinkFilterInput>({});

  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedAssetLink, setSelectedAssetLink] = useState<AssetLink | null>(
    null
  );

  const {
    assetLinks,
    total,
    loading,
    error,
    generateAssetLink,
    updateAssetLink,

    generateQRCode,
    deleteQRCode,
    uploadCustomQR,
    deleteCustomQR,
    getAnalytics,
    refresh,
  } = useAssetLinks({
    assetId: asset.id,
    filter,
    autoRefresh: true,
    refreshInterval: 30000,
  });

  const handleGenerateFirstLink = useCallback(async () => {
    try {
      await generateAssetLink({
        assetId: asset.id,
        metadata: {},
      });
      // Refresh the list to show the new link
      refresh();
    } catch (error) {
      console.error("Error generating asset link:", error);
    }
  }, [asset.id, generateAssetLink, refresh]);

  const handleGenerateQRCode = useCallback(
    async (assetLinkId: string, size: QRCodeSize = "MEDIUM") => {
      try {
        // Convert QRCodeSize to number
        const sizeMap = { SMALL: 200, MEDIUM: 300, LARGE: 400 };
        const numericSize = sizeMap[size];

        await generateQRCode({
          linkId: assetLinkId,
          size: numericSize,
          format: "SVG",
          customization: {
            errorCorrectionLevel: "H",
          },
        });

        // Refresh the data to show the new QR code
        refresh();
      } catch (error) {
        console.error("Failed to generate QR code:", error);
        throw error; // Re-throw to allow the loading state to handle it
      }
    },
    [generateQRCode, refresh]
  );

  const handleViewAnalytics = useCallback(
    async (assetLinkId: string) => {
      setSelectedAssetLink(
        assetLinks.find((link) => link.id === assetLinkId) || null
      );
      setShowAnalyticsModal(true);
    },
    [assetLinks]
  );

  if (loading && assetLinks.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading asset links...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading asset links
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error.message}</p>
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="bg-red-100 px-2 py-1 text-sm font-medium text-red-800 rounded-md hover:bg-red-200"
                onClick={refresh}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Stats */}
      {/* <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Scans
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {assetLinks.reduce(
                      (sum, link) => sum + (link.scanCount || 0),
                      0
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Link Clicks
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {assetLinks.reduce(
                      (sum, link) => sum + (link.clickCount || 0),
                      0
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    QR Codes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {assetLinks.reduce(
                      (sum, link) =>
                        sum + link.qrCodes.length + link.customQRCodes.length,
                      0
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Asset Links List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          {assetLinks.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No asset links
              </h3>
              {/* <p className="mt-1 text-sm text-gray-500">
                Generate a trackable link for this asset to start tracking
                interactions.
              </p> */}
              <div className="mt-6">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={handleGenerateFirstLink}
                >
                  <svg
                    className="-ml-1 mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Generate Link
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {assetLinks.map((assetLink) => (
                <AssetLinkCard
                  key={assetLink.id}
                  assetLink={assetLink}
                  onGenerateQR={handleGenerateQRCode}
                  onViewAnalytics={handleViewAnalytics}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Asset Link Card Component
interface AssetLinkCardProps {
  assetLink: AssetLink;
  onGenerateQR: (assetLinkId: string, size: QRCodeSize) => void;
  onViewAnalytics: (assetLinkId: string) => void;
}

function AssetLinkCard({
  assetLink,
  onGenerateQR,
  onViewAnalytics,
}: AssetLinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(assetLink.trackableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error("Failed to copy link:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = assetLink.trackableUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGenerateQR = async () => {
    setGeneratingQR(true);
    try {
      await onGenerateQR(assetLink.id, "MEDIUM");
    } catch (error) {
      console.error("Failed to generate QR code:", error);
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleDownloadQR = async (downloadUrl: string, filename?: string) => {
    try {
      // Fetch the file
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch QR code: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `qr-code-${assetLink.id}.svg`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download QR code:", error);
      // Fallback: try opening in new tab
      window.open(downloadUrl, "_blank");
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  assetLink.status === "ACTIVE"
                    ? "bg-green-100 text-green-800"
                    : assetLink.status === "INACTIVE"
                    ? "bg-gray-100 text-gray-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {LINK_STATUS_LABELS[assetLink.status]}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                Link: {assetLink.trackableUrl}
              </p>
              <p className="text-sm font-medium text-blue-600 truncate">
                QR: {assetLink.qrTrackableUrl}
              </p>
              <p className="text-sm text-gray-500">
                Short code: {assetLink.shortCode}
                {/* • {assetLink.clickCount}{" "} clicks • {assetLink.scanCount} scans */}
              </p>
            </div>
          </div>
          {/* {assetLink.metadata.description && (
            <p className="mt-2 text-sm text-gray-600">
              {assetLink.metadata.description}
            </p>
          )} */}
        </div>
        <div className="flex items-center space-x-2">
          {/* <button
            type="button"
            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
            onClick={() => onViewAnalytics(assetLink.id)}
          >
            Analytics
          </button> */}
          <button
            type="button"
            className={`text-sm font-medium ${
              copied
                ? "text-green-700 bg-green-50 px-2 py-1 rounded"
                : "text-green-600 hover:text-green-900"
            }`}
            onClick={handleCopyLink}
            disabled={copied}
          >
            {copied ? "Copied!" : "Copy Link"}
          </button>
          {assetLink.qrCodes.length === 0 ? (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center space-x-1"
              onClick={handleGenerateQR}
              disabled={generatingQR}
            >
              {generatingQR ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  <span>Generating...</span>
                </>
              ) : (
                <span>Generate QR</span>
              )}
            </button>
          ) : (
            <span className="text-sm text-green-600 font-medium">
              ✓ QR Code Generated
            </span>
          )}
        </div>
      </div>

      {/* QR Codes */}
      {(assetLink.qrCodes.length > 0 || assetLink.customQRCodes.length > 0) && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            {assetLink.qrCodes.map((qrCode) => (
              <div key={qrCode.id} className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-100 rounded border flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {/* <span className="text-xs text-gray-500">
                  {QR_CODE_SIZE_LABELS[qrCode.size]}
                </span> */}
                <button
                  onClick={() =>
                    handleDownloadQR(
                      qrCode.downloadUrl,
                      `qr-code-${assetLink.id}.svg`
                    )
                  }
                  className="text-xs text-blue-600 hover:text-blue-900 underline"
                >
                  Download QR code
                </button>
              </div>
            ))}
            {assetLink.customQRCodes.map((customQR) => (
              <div key={customQR.id} className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded border flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-xs text-gray-500">
                  {customQR.filename}
                </span>
                <a
                  href={customQR.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-900"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
